import type { CSSProperties, ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { cn } from "../../lib/utils";
import { ColumnDisplayPopover } from "./ColumnDisplayPopover";
import { DataTableProvider } from "./DataTableContext";
import { useDataTablePreferences } from "./useDataTablePreferences";
import type { TableColumnDef } from "./types";

export interface DataTableProps<T> {
  tableId: string;
  columns: TableColumnDef<T>[];
  data: T[];
  getRowKey: (row: T) => string | number;
  loading?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  /** Title on the left; column settings gear on the far right */
  title?: ReactNode;
  toolbar?: ReactNode;
  leadingColumnCount?: number;
  renderLeadingHead?: () => ReactNode;
  renderLeadingCell?: (row: T, rowIndex: number) => ReactNode;
  rowClassName?: (row: T) => string | undefined;
  headerRowClassName?: string;
  showColumnDisplay?: boolean;
  /** Use when parent wraps with DataTableProvider and DataTableColumnSettings in CardHeader */
  embedded?: boolean;
  hideToolbar?: boolean;
  className?: string;
}

function DataTableInner<T>({
  columns,
  data,
  getRowKey,
  loading = false,
  emptyMessage = "No records found.",
  loadingMessage = "Loading...",
  title,
  toolbar,
  leadingColumnCount = 0,
  renderLeadingHead,
  renderLeadingCell,
  rowClassName,
  headerRowClassName,
  showColumnDisplay = true,
  hideToolbar = false,
  className,
}: Omit<DataTableProps<T>, "tableId" | "embedded">) {
  const prefs = useDataTablePreferences<T>();
  if (!prefs) {
    throw new Error("DataTable must be used inside DataTableProvider");
  }

  const {
    displayColumns,
    menuColumns,
    setVisible,
    setAllVisible,
    setPin,
    reorderColumns,
    reset,
    getStickyStyle,
    getPinCellClassName,
  } = prefs;

  const columnLabels = Object.fromEntries(
    columns.filter(c => !c.hideFromDisplay).map(c => [c.id, c.label])
  );
  const lockVisibilityIds = new Set(
    columns.filter(c => c.lockVisibility).map(c => c.id)
  );

  const visibleDataColumns = displayColumns.filter(c => !c.hideFromDisplay);
  const totalColSpan = leadingColumnCount + visibleDataColumns.length;

  const getColumnWidthStyle = (
    col: TableColumnDef<T>
  ): CSSProperties | undefined => {
    if (col.width === undefined || col.width === null) {
      return undefined;
    }
    return {
      width: col.width,
      minWidth: col.width,
      maxWidth: col.width,
    };
  };

  const showToolbarRow =
    !hideToolbar &&
    (title ?? toolbar ?? (showColumnDisplay && menuColumns.length > 0));

  return (
    <div className={cn(className)}>
      <div className="w-full min-w-0 overflow-x-auto rounded-md border">
        {showToolbarRow && (
          <div className="flex items-center justify-between gap-2 border-b bg-muted/20 px-3 py-1.5">
            {title ? (
              <div className="min-w-0 truncate text-sm font-semibold text-foreground">
                {title}
              </div>
            ) : (
              <span className="flex-1" />
            )}
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {toolbar}
              {showColumnDisplay && menuColumns.length > 0 && (
                <ColumnDisplayPopover
                  columns={menuColumns}
                  labels={columnLabels}
                  lockVisibilityIds={lockVisibilityIds}
                  onVisibleChange={setVisible}
                  onAllVisibleChange={setAllVisible}
                  onPinChange={setPin}
                  onReorder={reorderColumns}
                  onReset={reset}
                />
              )}
            </div>
          </div>
        )}
        <Table className="w-full min-w-full table-fixed border-0">
          <TableHeader>
            <TableRow className={headerRowClassName}>
              {renderLeadingHead?.()}
              {visibleDataColumns.map(col => (
                <TableHead
                  key={col.id}
                  className={cn(
                    col.headerClassName,
                    "whitespace-nowrap",
                    getPinCellClassName(col.id)
                  )}
                  style={{
                    ...getColumnWidthStyle(col),
                    ...getStickyStyle(col.id),
                  }}
                >
                  {col.renderHeader ? col.renderHeader() : col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(totalColSpan, 1)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {loadingMessage}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(totalColSpan, 1)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => (
                <TableRow key={getRowKey(row)} className={rowClassName?.(row)}>
                  {renderLeadingCell?.(row, rowIndex)}
                  {visibleDataColumns.map(col => (
                    <TableCell
                      key={col.id}
                      className={cn(
                        col.cellClassName,
                        col.width !== null && "max-w-0 overflow-hidden",
                        getPinCellClassName(col.id)
                      )}
                      style={{
                        ...getColumnWidthStyle(col),
                        ...getStickyStyle(col.id),
                      }}
                    >
                      {col.renderCell(row, rowIndex)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function DataTable<T>({
  tableId,
  columns,
  embedded,
  ...rest
}: DataTableProps<T>) {
  if (embedded) {
    return <DataTableInner columns={columns} {...rest} />;
  }

  return (
    <DataTableProvider tableId={tableId} columns={columns}>
      <DataTableInner columns={columns} {...rest} />
    </DataTableProvider>
  );
}
